import { inject, Injectable } from '@angular/core';
import { OperationTypeByAPIMap, ServiceTypeMetadataMap, SINGLE_QUOTES_REGEX } from '@common/constants';
import { BooleanOperator, TaskAction } from '@common/enums';
import { DomainCancelAllPayload, TaskIds, TaskIdsResponse, TaskManagerOdata, UserCancelAllPayload } from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { SharedOdataService } from '@common/services';
import { AbilityService } from '@common/services/auth/ability.service';
import { AuthContextService } from '@common/services/auth/auth-context.service';
import { TaskManagerTag, TaskManagerTagsMap } from '@common/services/smart-search';
import { FilterOptions } from '@common/types';
import { filterByWords } from '@common/utils';
import { containsWrapper, getFilterByContains, getFilterByEq, getFilterByEqFromEnum } from '@common/utils/functions/search';
import { SmartSearchModel, SmartSearchModelField } from 'mbs-ui-kit';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TaskManagerService extends SharedOdataService<TaskManagerOdata> {
  private readonly ability = inject(AbilityService);
  private readonly authContext = inject(AuthContextService);

  readonly #serviceType = TaskManagerTagsMap.get(TaskManagerTag.ServiceType);
  readonly #status = TaskManagerTagsMap.get(TaskManagerTag.Status);
  readonly #task = TaskManagerTagsMap.get(TaskManagerTag.Task);
  readonly #userName = TaskManagerTagsMap.get(TaskManagerTag.UserName);

  protected readonly authUserId = this.authContext.id;
  protected readonly authDomainId = this.authContext.domainId;

  protected readonly isAdmin = this.ability.isAdmin;

  constructor() {
    super('Tasks');
  }

  fetchAction(id: string | string[] | undefined, action: TaskAction): Observable<void> {
    switch (action) {
      case TaskAction.Resume:
      case TaskAction.Restart: {
        const odataService = this.odataFactory.CreateService<TaskManagerOdata>(`Tasks(${id})/Restart`);

        return odataService.Post<void>(null).Exec();
      }
      case TaskAction.Pause: {
        const odataService = this.odataFactory.CreateService<TaskManagerOdata>(`Tasks(${id})/Pause`);

        return odataService.Post<void>(null).Exec();
      }
      case TaskAction.Cancel: {
        const odataService = this.odataFactory.CreateService<TaskManagerOdata>(`Tasks(${id})/Cancel`);

        return odataService.Post<void>(null).Exec();
      }
      case TaskAction.CancelAll: {
        return this.cancelAll() as unknown as Observable<void>;
      }
      case TaskAction.CancelSelected: {
        const ids = Array.isArray(id) ? id : [id];
        const payload: TaskIds = { TaskIds: ids };

        return this.cancelSelected(payload) as unknown as Observable<void>;
      }
      default:
        throw new Error(`Caught error manually: Unknown action: "${action}"`);
    }
  }

  updateFilter(searchObj: SmartSearchModel): void {
    const filter: string[] = [];

    if (searchObj[this.#userName.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#userName.tag] as SmartSearchModelField[],
        prop: this.#userName.prop
      };

      filter.push(getFilterByContains(options));
    }

    if (searchObj[this.#task.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#task.tag] as SmartSearchModelField[],
        prop: this.#task.prop,
        enumItems: OperationTypeByAPIMap
      };

      filter.push(getFilterByEqFromEnum(options));
    }

    if (searchObj[this.#serviceType.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#serviceType.tag] as SmartSearchModelField[],
        prop: this.#serviceType.prop,
        enumItems: ServiceTypeMetadataMap
      };

      filter.push(getFilterByEqFromEnum(options));
    }

    if (searchObj[this.#status.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#status.tag] as SmartSearchModelField[],
        prop: this.#status.prop
      };

      filter.push(getFilterByEq(options));
    }

    if (searchObj.words?.filter(Boolean)) {
      const term = filterByWords(searchObj).replace(SINGLE_QUOTES_REGEX, '');

      filter.push(containsWrapper(this.#userName.prop, term));
    }

    this.filter = filter.length > 0 ? filter.join(` ${BooleanOperator.And} `) : '';
  }

  cancelSelected(payload: TaskIds): Observable<TaskIdsResponse[]> {
    const odataService = this.odataFactory.CreateService<TaskIdsResponse[]>(`Tasks/CancelSelected`);

    return odataService.Post<TaskIds>(payload).Exec() as unknown as Observable<TaskIdsResponse[]>;
  }

  cancelAll(): Observable<TaskIdsResponse[]> {
    return this.isAdmin() ? this.domainCancelAll(this.authDomainId()) : this.userCancelAll(this.authUserId());
  }

  private domainCancelAll(id: string): Observable<TaskIdsResponse[]> {
    const odataService = this.odataFactory.CreateService<TaskIdsResponse[]>(`Tasks/CancelAll`);

    return odataService.Post<DomainCancelAllPayload>({ DomainId: id }).Exec() as unknown as Observable<TaskIdsResponse[]>;
  }

  private userCancelAll(id: string): Observable<TaskIdsResponse[]> {
    const odataService = this.odataFactory.CreateService<TaskIdsResponse[]>(`Tasks/CancelAll`);

    return odataService.Post<UserCancelAllPayload>({ UserId: id }).Exec() as unknown as Observable<TaskIdsResponse[]>;
  }

  getTasks(): Observable<ODataPagedResult<TaskManagerOdata>> {
    const isAdmin = this.isAdmin();
    const id = isAdmin ? this.authDomainId() : this.authUserId();
    const prefix = isAdmin ? 'Domains' : 'BackupUsers';
    const url = `/${prefix}(${id})/Tasks`;

    const odataService = this.odataFactory.CreateService<TaskManagerOdata>(url);
    return this.fetchData<TaskManagerOdata>(odataService);
  }
}
